'use client'

/**
 * The side navigation's account row, wired (#15618, #15688): the gate inputs
 * the org's user-menu layout resolves against, and the entry context every
 * configurable entry opens through — the SAME command bar, shortcuts drawer
 * and feedback modal the header owns, read through `app-chrome-context`.
 *
 * Shared by `AppSidebar` and the admin side navigation
 * (`admin-side-nav.tsx`), so the org switcher, the configured sections
 * and Log out behave identically in both.
 */

import { useDemoBarVisibility } from '@/components/demo-bar-visibility'
import type { EntryActionContext } from '@/components/entry-actions'
import { useFeedbackEntry } from '@/components/feedback/use-feedback-entry'
import { useAppChromeOverlay } from '@/contexts/app-chrome-context'
import { usePresentationMode } from '@/contexts/presentation-mode-context'
import { useEnterDemoMode } from '@/hooks/use-enter-demo-mode'
import type { AppChromeHeaderProps } from '@/lib/app-chrome/types'
import {
  buildEntryAvailability,
  type ChromeGateInputs,
} from '@/lib/app-chrome/nav-availability'
import { deserializeVisibleSections } from '@/lib/onboarding/visible-sections-client'
import { resolveUserMenuLayout } from '@/lib/org-nav/resolve'

import { AccountMenu, type AccountMenuUser } from './account-menu'

/** What the sidebar needs from `resolveAppChrome` — all but the demo banner. */
export type AppSidebarChrome = Omit<AppChromeHeaderProps, 'demoBannerProps'>

/** The header's gate inputs, from the chrome and the viewer's role. */
export function chromeGateInputs(
  chrome: AppSidebarChrome,
  role: AccountMenuUser['role'],
): ChromeGateInputs {
  const visibleSections = deserializeVisibleSections(chrome.visibleSections)
  const canSee = (section: string) =>
    visibleSections === 'all' || visibleSections.has(section)
  return {
    ...chrome,
    canSee,
    isOnboardingComplete: visibleSections === 'all',
    commandBarAllowed: canSee('command-bar'),
    role,
  }
}

/**
 * Every action's handler (#15689) — the sidebar's action rows' and the
 * account menu's, so an entry runs the same way wherever it is placed.
 */
export function useEntryActionContext(
  chrome: AppSidebarChrome,
): EntryActionContext {
  const [, setCommandBarOpen] = useAppChromeOverlay('commandBar')
  const [, setShortcutsOpen] = useAppChromeOverlay('shortcuts')
  const feedback = useFeedbackEntry()
  const { isPresentationMode, toggle: togglePresentationMode } =
    usePresentationMode()
  const { isVisible: isDemoBarVisible, toggle: toggleDemoBar } =
    useDemoBarVisibility()
  const { isEnteringDemo, enterDemoMode } = useEnterDemoMode()

  return {
    openCommandBar: () => setCommandBarOpen(true),
    openFeedback: feedback.open,
    openShortcuts: () => setShortcutsOpen(true),
    isPresentationMode,
    togglePresentationMode,
    isInDemoMode: chrome.isInDemoMode,
    isEnteringDemo,
    onEnterDemoMode: () => void enterDemoMode(),
    isDemoBarVisible,
    toggleDemoBar,
  }
}

export function SidebarAccountMenu({
  user,
  chrome,
}: {
  user: AccountMenuUser
  chrome: AppSidebarChrome
}) {
  const actionContext = useEntryActionContext(chrome)

  const layout = resolveUserMenuLayout(
    chrome.userMenuLayout,
    buildEntryAvailability(chromeGateInputs(chrome, user.role)),
  )

  return (
    <AccountMenu
      user={user}
      orgs={chrome.orgs}
      currentOrgId={chrome.currentOrgId}
      layout={layout}
      entryContext={{
        ...actionContext,
        pendingReviewCount: chrome.pendingReviewCount,
      }}
    />
  )
}
